import { create } from 'zustand';
import { PREF_DEFAULTS } from '@maker-toolchest/shared';

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Add Model Sidebar
  isAddModelSidebarOpen: boolean;
  addModelSidebarData: { onSuccess?: (modelId: string) => void } | null;
  openAddModelSidebar: (data?: { onSuccess?: (modelId: string) => void }) => void;
  closeAddModelSidebar: () => void;

  // Add Job Sidebar
  isAddJobSidebarOpen: boolean;
  addJobSidebarData: { defaultModelId?: string } | null;
  openAddJobSidebar: (data?: { defaultModelId?: string }) => void;
  closeAddJobSidebar: () => void;

  // Collection Sidebar
  isCollectionSidebarOpen: boolean;
  collectionSidebarData: { collection?: any; defaultParentId?: string; parentCollectionName?: string } | null;
  openCollectionSidebar: (data?: { collection?: any; defaultParentId?: string; parentCollectionName?: string }) => void;
  closeCollectionSidebar: () => void;

  // Auto-expand parent after creating sub-collection
  autoExpandCollectionId: string | null;
  setAutoExpandCollectionId: (id: string | null) => void;

  // Modal
  activeModal: string | null;
  modalData: unknown;
  openModal: (modal: string, data?: unknown) => void;
  closeModal: () => void;

  // Toast notifications (simple version)
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;

  // Collections Filter Sidebar (default true = open)
  collectionsFilterOpen: boolean;
  toggleCollectionsFilter: () => void;
  setCollectionsFilterOpen: (open: boolean) => void;

  // Selected Collections for Filtering (single-select)
  selectedCollectionIds: string[];
  setSelectedCollectionIds: (ids: string[]) => void;
  selectCollection: (id: string) => void; // Single-select: replaces current selection
  clearCollectionSelection: () => void;
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
}

/**
 * UI Store - Manages application UI state
 *
 * Note: Persistent preferences (sidebarCollapsed, collectionsFilterOpen) are now
 * synced with the database via the preferences API. Use the useUIPreferences hook
 * to initialize and sync these values.
 */
export const useUIStore = create<UIState>((set) => ({
  // Sidebar
  sidebarCollapsed: PREF_DEFAULTS.UI.SIDEBAR_COLLAPSED,
  toggleSidebar: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setSidebarCollapsed: (collapsed) =>
    set({ sidebarCollapsed: collapsed }),

  // Add Model Sidebar
  isAddModelSidebarOpen: false,
  addModelSidebarData: null,
  openAddModelSidebar: (data) =>
    set({ isAddModelSidebarOpen: true, addModelSidebarData: data || null }),
  closeAddModelSidebar: () =>
    set({ isAddModelSidebarOpen: false, addModelSidebarData: null }),

  // Add Job Sidebar
  isAddJobSidebarOpen: false,
  addJobSidebarData: null,
  openAddJobSidebar: (data) =>
    set({ isAddJobSidebarOpen: true, addJobSidebarData: data || null }),
  closeAddJobSidebar: () =>
    set({ isAddJobSidebarOpen: false, addJobSidebarData: null }),

  // Collection Sidebar
  isCollectionSidebarOpen: false,
  collectionSidebarData: null,
  openCollectionSidebar: (data) =>
    set({ isCollectionSidebarOpen: true, collectionSidebarData: data || null }),
  closeCollectionSidebar: () =>
    set({ isCollectionSidebarOpen: false, collectionSidebarData: null }),

  // Auto-expand parent after creating sub-collection
  autoExpandCollectionId: null,
  setAutoExpandCollectionId: (id) =>
    set({ autoExpandCollectionId: id }),

  // Modal
  activeModal: null,
  modalData: null,
  openModal: (modal, data) =>
    set({ activeModal: modal, modalData: data }),
  closeModal: () =>
    set({ activeModal: null, modalData: null }),

  // Toasts
  toasts: [],
  addToast: (toast) =>
    set((state) => ({
      toasts: [
        ...state.toasts,
        { ...toast, id: crypto.randomUUID() },
      ],
    })),
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  // Collections Filter Sidebar
  collectionsFilterOpen: PREF_DEFAULTS.UI.COLLECTIONS_FILTER_OPEN,
  toggleCollectionsFilter: () =>
    set((state) => ({ collectionsFilterOpen: !state.collectionsFilterOpen })),
  setCollectionsFilterOpen: (open) =>
    set({ collectionsFilterOpen: open }),

  // Selected Collections for Filtering (single-select)
  selectedCollectionIds: [],
  setSelectedCollectionIds: (ids) =>
    set({ selectedCollectionIds: ids }),
  selectCollection: (id) =>
    set((state) => ({
      // If clicking the already-selected collection, deselect it
      selectedCollectionIds: state.selectedCollectionIds.length === 1 && state.selectedCollectionIds[0] === id
        ? []
        : [id],
    })),
  clearCollectionSelection: () =>
    set({ selectedCollectionIds: [] }),
}));
